import * as React from 'react';
import { ensureArray } from '@agilepoint/utils-array';
import { ICollapsibleProps, ICollapsibleItemId, ICollapsibleItemProps } from './collapsible-types';
import { CollapsibleContext } from './collapsible-context';
import { useMicroStore } from '../../hooks';
import { CollapsibleWrapper, CollapsibleSurface } from './parts';
import { Item } from './collapsible-item';

function getActiveItems(isAccordion: boolean, defaultActiveItems: ICollapsibleItemId = [], activeItems?: ICollapsibleItemId) {
    const items = ensureArray(activeItems || defaultActiveItems);
    return isAccordion ? items.slice(0, 1) : items;
}

function updateActiveKeys(isAccordion: boolean, items: string[], itemKey: string): string[] {
    const itemIndex = items.indexOf(itemKey);
    if (isAccordion) {
        return items[0] === itemKey ? [] : [itemKey];
    }

    if (itemIndex === -1) {
        items.push(itemKey);
        return items;
    }

    items.splice(itemIndex, 1);
    return items;
}

function getElementDOMRect(elementRef: React.RefObject<HTMLDivElement>): DOMRect {
    return elementRef.current!.getBoundingClientRect();
}

export function Collapsible(props: ICollapsibleProps) {
    const { className, accordion = false, onChange, children, activeItems: activeItemsFromProps, defaultActiveItems, arrowPosition } = props;
    const { items, remove, upsert, detachStore } = useMicroStore<ICollapsibleItemProps>({ disableDispatchAggregator: true });
    const itemsFromProps = React.useMemo(() => getActiveItems(accordion, defaultActiveItems, activeItemsFromProps), [accordion, defaultActiveItems, activeItemsFromProps]);
    const [activeItems, setActiveItems] = React.useState(itemsFromProps);
    const surfaceElementRef = React.useRef<HTMLDivElement>(null);
    const wrapperElementRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => () => detachStore(), [detachStore]);

    React.useEffect(() => {
        if (activeItemsFromProps !== undefined) {
            setActiveItems(ensureArray(activeItemsFromProps));
        }
    }, [activeItemsFromProps]);

    const getItemInView = React.useCallback((activeItemRef: React.RefObject<HTMLDivElement>, isExpanded: boolean) => {
        const { top, bottom } = getElementDOMRect(activeItemRef);
        const { top: parentTop, bottom: parentBottom } = getElementDOMRect(surfaceElementRef);
        const { scrollTop } = surfaceElementRef.current!;

        if ((bottom > parentBottom || top < parentTop) && isExpanded) {
            const availableSpaceTop = (top - parentTop) - 2;
            const availableSpaceBottom = bottom - parentBottom;

            surfaceElementRef.current?.scrollTo({
                behavior: 'smooth',
                top: availableSpaceBottom > availableSpaceTop ? scrollTop + availableSpaceTop : scrollTop + availableSpaceBottom
            });
        }
        else if (!isExpanded && !accordion) {
            activeItemRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest'
            });
        }
    }, [accordion]);

    const _onChange = React.useCallback((itemKey: string) => {
        if (onChange) {
            onChange(itemKey);
        }

        if (activeItemsFromProps === undefined) {
            setActiveItems(updateActiveKeys(accordion, [...activeItems], itemKey));
        }
    }, [onChange, activeItemsFromProps, accordion, activeItems]);

    const _canGetItemInView = React.useCallback(() => accordion || activeItems.length === 1, [accordion, activeItems]);

    const collapsibleItemRenderer = React.useCallback((collapsibleItemProps) => {
        const isExpanded = activeItems.includes(collapsibleItemProps.id);
        return (
            <Item
                key={collapsibleItemProps.id}
                arrowPosition={arrowPosition}
                {...collapsibleItemProps}
                onChange={_onChange}
                isExpanded={isExpanded}
                canGetItemInView={_canGetItemInView}
                getItemInView={getItemInView} />
        );
    }, [activeItems, _onChange, _canGetItemInView, getItemInView, arrowPosition]);

    const renderActiveItems = React.useCallback(() => items.map(collapsibleItemRenderer), [items, collapsibleItemRenderer]);

    return (
        <CollapsibleContext.Provider value={{ upsert, remove }}>
            <CollapsibleSurface ref={surfaceElementRef}>
                <CollapsibleWrapper ref={wrapperElementRef} className={className}>
                    {children}
                    {renderActiveItems()}
                </CollapsibleWrapper>
            </CollapsibleSurface>
        </CollapsibleContext.Provider>
    );
}
